import '../documents.css';

import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { allUserConfigsState } from '../../../recoil/atoms/allUserConfigsState';
import { navigationPropsState } from '../../../recoil/atoms/navigationPropsState';
import { useSetNavigationProps } from '../../../recoil/hooks/useSetNavigationProps';

import { Rating } from '@mui/material';
import { translate } from '../../../../common/language/translations';
import { getTenantColor } from '../../../static/tenantConfigs';
import { Toggle } from '../../controls/input/Toggle';
import { dateStringToTextFormat } from '../../../utils/dateUtils';
import { PlayerReport } from '../../../types/report';
import { getDisplayPlayerName, getShortUserName } from '../../../utils/nameUtils';
import { getReportIcon } from '../../../utils/iconUtils';


interface ExistingReportsProps {
  reports: PlayerReport[];
  selectedReport: PlayerReport | undefined;
  handleReportClicked: (report: PlayerReport | undefined) => void;
  isPlayerView?: boolean;
}

export const ExistingReports: React.FC<ExistingReportsProps> = ({ reports, handleReportClicked, selectedReport, isPlayerView }) => {

  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);

  const navigationProps = useRecoilValue(navigationPropsState);
  const updateNavigationProps = useSetNavigationProps();


  const getReportClassName = (report: PlayerReport) => {
    let baseClassName = 'existing-document';

    baseClassName += (isPlayerView ? ' existing-report-player-view' : ' existing-report-archive-view');

    if (report.isDeleted) {
      baseClassName += ' existing-document-deleted';
    }
    if (report.id === selectedReport?.id) {
      baseClassName += ' existing-document-selected';
    }

    if (userConfig && report.userEmail === userConfig.email) {
      if (isPlayerView) {
        baseClassName += ' existing-report-player-view-by-user';
      }
      else {
        baseClassName += ' existing-report-archive-view-by-user';
      }
    }

    return baseClassName;
  };


  const tenantColor = getTenantColor(userConfig?.club ?? '');


  return (
    <div className='existing-documents-container'>

      <div className='existing-documents-title'>
        {translate('existingReports', userConfig?.language)}
      </div>

      <div className='existing-documents-divider existing-documents-top-divider' />

      <div className='existing-documents-section existing-documents-section-single-toggle'>
        {reports.length === 0 && (
          <div className='no-existing-documents-title'>
            {translate('noExistingReports', userConfig?.language)}
          </div>
        )}
        {reports.map((report: PlayerReport) => {
          if (!navigationProps.showDeletedReports && report.isDeleted) return null;

          const isRatingReport = report.reportType === 'ScoutingReport' || report.reportType === 'MatchReport';

          const isUnkownUser = !allUsersWithSameTenant[report.userEmail];

          return (
            <div
              key={report.id}
              className={getReportClassName(report)}
              onClick={() => handleReportClicked(report)}
            >
              {getReportIcon(report.reportType, 22, '#fdfdfd')}

              <div className='existing-document-vertical-divider' style={{ backgroundColor: tenantColor }} />

              <div className='existing-document-info'>
                <div className='existing-document-row'>
                  <div className={'existing-document-title' + (isPlayerView && !isRatingReport ? ' existing-report-title-player-view-comment' : '')}>
                    {isPlayerView
                      ? translate(report.reportType, userConfig?.language)
                      : getDisplayPlayerName(report.playerName, 14)}
                  </div>
                  <div
                    className={
                      'existing-report-user'
                      + (isPlayerView && !isRatingReport ? ' existing-report-user-player-view-comment' : '')
                      + (isUnkownUser ? ' existing-report-unknown-user' : '')
                    }
                  >
                    {isUnkownUser
                      ? translate('unknownUserShort', userConfig?.language)
                      : getShortUserName(allUsersWithSameTenant[report.userEmail].name)
                    }
                  </div>
                </div>
                <div className='existing-document-row'>
                  <div className='existing-report-player-view-rating'>
                    {isPlayerView && isRatingReport && (
                      <Rating
                        name={report.id + '_rating'}
                        value={report.reportFields.overallScore ?? 0}
                        max={5}
                        precision={0.5}
                        sx={{ fontSize: 14, color: tenantColor, '& .MuiRating-iconEmpty': { color: '#bbbdc2' } }}
                        readOnly={true}
                      />
                    )}
                    {!isPlayerView && (
                      <div className='existing-report-archive-view-player-name'>
                        {translate(report.reportType, userConfig?.language)}
                      </div>
                    )}
                  </div>
                  <div className={'existing-report-date' + (isPlayerView && !isRatingReport ? ' existing-report-date-player-view-comment' : '')}>
                    {dateStringToTextFormat(report.date.split('T')[0])}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className='existing-documents-divider existing-documents-bottom-divider-single-toggle' />

      <div className='existing-document-toggle-container existing-document-toggle-bottom-container'>
        <div className='existing-document-toggle-section'>
          <div className='existing-document-toggle-title'>
            {translate('showDeletedReports', userConfig?.language)}
          </div>
          <div className='existing-document-toggle'>
            <Toggle
              isToggled={navigationProps.showDeletedReports ?? false}
              setIsToggled={() => updateNavigationProps({ showDeletedReports: !(navigationProps.showDeletedReports ?? false) })}
            />
          </div>
        </div>
      </div>

    </div>
  );
};
